import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { tap, map, switchMap, catchError} from 'rxjs/operators';
import { AuthorizationService } from '@myapp/modules/fwcore/services';
import { RequestAccess } from '../app/modules/fwcore/models/messages';
import { PermissionCodeService } from '../app/features/prob/services/pb-permission-code.service';
import { OktaWrapperService } from '../services/framework/okta-wrapper.service';


export const AfGuardFnPageAccess = (route: ActivatedRouteSnapshot, redirectRoute?: string):
  | boolean
  | UrlTree
  | Promise<boolean | UrlTree>
  | Observable<boolean | UrlTree> => {
  const authService: AuthorizationService = inject(AuthorizationService);
  const router: Router = inject(Router);

  let req: RequestAccess = new RequestAccess();
  req.route = buildRoutePath(route);

  // check that the path starts with a '/'
  req.route = req.route.startsWith('/') ? req.route : '/' + req.route;

  if (!redirectRoute) {
    redirectRoute = '/error/not-authorized';
  }

  return authService
    .hasAccess$(req)
    .pipe(
      map(result => {
        if (!result.isLicensed) {
          return router.createUrlTree(['no-license']);
        }
        else if (result.success) {
          return true;
        }
        else {
          return router.createUrlTree([redirectRoute]);
        }
      })
    );

}

export const AfGuardFnLicensed = (): Observable<boolean | UrlTree> => {
  const permService: PermissionCodeService = inject(PermissionCodeService);
  const authService: AuthorizationService = inject(AuthorizationService);
  const oktaWrapper: OktaWrapperService = inject(OktaWrapperService);
  const router: Router = inject(Router);

  return permService.licenseValid$().pipe(
    switchMap(valid => {
      if (valid === true) {
        return of(true);
      }

      const email = (oktaWrapper.getUser()?.email ?? '').toLowerCase();

      if (!email.endsWith('@isg-one.com')) {
        return of(router.createUrlTree(['no-license']));
      }

      const req = new RequestAccess();

      return authService.selfEnroll$(req).pipe(
        switchMap(enrollResult => {
          if (!enrollResult.success) {
            return of(false);
          }

          // Both cache layers must be cleared, or the re-check below just
          // replays the stale "not licensed" result from hasAccess$'s own cache.
          authService.clearAuths();
          permService.resetCache();

          return permService.licenseValid$();
        }),
        map(nowValid => nowValid === true ? true : router.createUrlTree(['no-license'])),
        catchError(() => of(router.createUrlTree(['no-license'])))
      );
    })
  );
}



export const AfGuardFnPermissionCodes = (route: ActivatedRouteSnapshot, redirectRoute?: string):
  Observable<boolean | UrlTree> => {
  const permService: PermissionCodeService = inject(PermissionCodeService);
  const router: Router = inject(Router);
  const codes: string[] = route.data['permCodes'] ?? [];
  const target = redirectRoute ?? '/error/not-authorized';

  if (codes.length === 0) {
    return of(router.createUrlTree([target]));
  }

  return permService.hasAnyCode$(buildRoutePath(route), codes)
    .pipe(map(granted => {
      if (granted === true) {
        return true;
      }

      return router.createUrlTree([target]);
    }));
}

/** Builds the absolute route path used by permission requests. */
export function buildRoutePath(route: ActivatedRouteSnapshot): string {
  const path = route.pathFromRoot
    .map(o => o.url.map(segment => segment.path).join('/'))
    .filter(p => p.length > 0)
    .join('/');

  if (path.startsWith('/')) {
    return path;
  }

  return '/' + path;
}

export const AfGuardFnOktaCallbackErrorCheck = (route: ActivatedRouteSnapshot): boolean | UrlTree => {
  const router: Router = inject(Router);

  const error = route.queryParamMap.get('error');
  const errorDescription = route.queryParamMap.get('error_description') ?? '';

  const isNotAssignedError = !!error &&
    errorDescription.toLowerCase().includes('not assigned to the client application');

  if (isNotAssignedError) {
    return router.createUrlTree(['no-license']);
  }

  return true;
};

